import { PageType } from '@/types';
import { monthNames, shortMonthNames } from '.';
import { uppercaseFirstLetter } from '@/utils/stringUtils';
import { findMatchingPreset, getDatePresets } from './presets';

const parseDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-');
  return {
    year: parseInt(year),
    month: parseInt(month),
    day: parseInt(day),
  };
};

/**
 * Formats a YYYY-MM or YYYY-MM-DD string as "November 2024" without constructing a Date.
 */
export const formatYearMonth = (yearMonth: string): string => {
  const { year, month } = parseDate(yearMonth);
  return `${uppercaseFirstLetter(monthNames[month - 1])} ${year}`;
};

/**
 * Formats a YYYY-MM or YYYY-MM-DD string as "Nov 2024" without constructing a Date.
 */
export const formatYearMonthShort = (yearMonth: string): string => {
  const { year, month } = parseDate(yearMonth);
  return `${shortMonthNames[month - 1]} ${year}`;
};

/**
 * Formats unique YYYY-MM values as "Nov 2024" or "Sep – Nov 2024".
 */
export const formatYearMonthShortRange = (yearMonths: string[]): string => {
  const sortedYearMonths = [...new Set(yearMonths)].sort();
  const firstYearMonth = sortedYearMonths[0];
  const lastYearMonth = sortedYearMonths[sortedYearMonths.length - 1];

  if (sortedYearMonths.length === 1) {
    return formatYearMonthShort(firstYearMonth);
  }

  const first = parseDate(firstYearMonth);
  const last = parseDate(lastYearMonth);

  if (first.year === last.year) {
    return `${shortMonthNames[first.month - 1]} – ${shortMonthNames[last.month - 1]} ${last.year}`;
  }

  return `${formatYearMonthShort(firstYearMonth)} – ${formatYearMonthShort(lastYearMonth)}`;
};

export const getDateString = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '—';
  }
  const dateOnly = dateString.split('T')[0];
  const { year, month, day } = parseDate(dateOnly);
  return `${day} ${shortMonthNames[month - 1]} ${year}`;
};

export const formatDateRange = (fromString: string, toString: string) => {
  const fromDate = parseDate(fromString);
  const toDate = parseDate(toString);

  // Same day
  if (fromString === toString) {
    return `${fromDate.day} ${uppercaseFirstLetter(monthNames[fromDate.month - 1])} ${fromDate.year}`;
  }

  // Same month and year
  if (fromDate.month === toDate.month && fromDate.year === toDate.year) {
    return `${fromDate.day} - ${toDate.day} ${uppercaseFirstLetter(monthNames[fromDate.month - 1])} ${fromDate.year}`;
  }

  // Different months, same year
  if (fromDate.month !== toDate.month && fromDate.year === toDate.year) {
    return `${uppercaseFirstLetter(monthNames[fromDate.month - 1])} ${fromDate.day} – ${uppercaseFirstLetter(monthNames[toDate.month - 1])} ${toDate.day}, ${toDate.year}`;
  }

  // Different months and years
  return `${uppercaseFirstLetter(monthNames[fromDate.month - 1])} ${fromDate.day}, ${fromDate.year} – ${uppercaseFirstLetter(monthNames[toDate.month - 1])} ${toDate.day}, ${toDate.year}`;
};

export type DateStringWithPresetResult = {
  prefix: string;
  body: string;
};

/**
 * Formats the date range string with the selected preset.
 * Returns prefix and body separately so the prefix can be hidden on small screens (e.g. with CSS).
 * @param from - The start date
 * @param to - The end date
 * @param pageType - The page type (earnings vs others) for prefix
 * @param selectedPreset - The selected preset
 * @returns Object with prefix ("Earned: " / "Dates: ") and body (preset name + range or range only)
 */
export const formatDateStringWithPreset = (
  from: string,
  to: string,
  pageType: PageType | undefined,
  selectedPreset?: { name: string }
): DateStringWithPresetResult => {
  const prefix = pageType && pageType === 'earnings' ? 'Earned: ' : 'Dates: ';
  const formattedRange = formatDateRange(from, to);
  const body = selectedPreset ? `${selectedPreset.name} (${formattedRange})` : formattedRange;
  return { prefix, body };
};

/**
 * Totals tab label for Content Performance: matching date preset name, otherwise the formatted range.
 */
export const formatTotalsTabLabel = (dateFrom: string, dateTo: string): string => {
  const preset = findMatchingPreset(dateFrom, dateTo, getDatePresets());
  const dateLabel = preset
    ? preset.name
    : formatDateStringWithPreset(dateFrom, dateTo, undefined).body;
  return `Totals (${dateLabel})`;
};
